import {ComponentStore} from '@ngrx/component-store';
import {ReportTableTrendsStoreState} from './report-table';
import {Inject, Injectable} from '@angular/core';
import {Observable, switchMap} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Serializer} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {AdRequestOptions, AdRResponseRow} from '../../interfaces/ad-reports';
import {ReportsRequestsService} from '../../services/reports-requests.service';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {ColDef, ICellRendererParams} from '@ag-grid-community/core';
import {MetricComponentObject} from '../../classes/metric-component-object';
import {AdRRequestParams} from '../../classes/ad-r-request-params';
import {EChartsOption} from 'echarts';
import {EchartsUtils} from '../../libraries/echarts-utils';
import {CurrencyService} from '../../services/currency.service';
import {DecimalService} from '../../services/decimal.service';

@Injectable()
export class ReportTableTrendsDialogStore extends ComponentStore<ReportTableTrendsStoreState> {

  public readonly loading$: Observable<boolean> = this.select((state: ReportTableTrendsStoreState): boolean => state.loading);
  public readonly dimension$: Observable<string> = this.select((state: ReportTableTrendsStoreState): string => state.dimension);
  public readonly rows$: Observable<Array<{ day: string, value: string, formatted_value: string }>> = this.select((state: ReportTableTrendsStoreState): Array<{ day: string, value: string, formatted_value: string }> => state.rows);
  public readonly chartOption$: Observable<EChartsOption> = this.select((state: ReportTableTrendsStoreState): EChartsOption => state.chartOption);

  private readonly _load: () => void = this.effect((params$: Observable<void>): Observable<any> => params$.pipe(
    tap((): void => this.patchState({ loading: true })),
    switchMap(() => this._adReportRequests.getTrends(
      this.data.dataset,
      this.data.uuid,
      this.data.token,
      this.data.value.type,
      this.data.value.slug,
      this.data.value.row,
      this.data.params,
      this.data.options
    )),
    tap((rows: Array<{ day: string, value: string, formatted_value: string }>): void => {
      this.patchState({
        loading: false,
        rows: rows,
        chartOption: this._initChart(rows)
      });
    })
  ));

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: ICellRendererParams & {
      dataset: Serializer<Site>,
      colDef: ColDef & { params: { metric: MetricComponentObject } }
      params: Partial<AdRRequestParams>,
      uuid: string | null,
      token: string | null,
      options: Partial<AdRequestOptions>,
      row: AdRResponseRow
    },
    private readonly _adReportRequests: ReportsRequestsService,
    private readonly _currencyS: CurrencyService,
    private readonly _decimalS: DecimalService,
  ) {
    super({
      loading: false,
      dimension: null,
      rows: [],
      chartOption: {}
    });

    this._load();
    this.patchState({
      dimension: this._getDimensions()
    })
  }

  private _initChart(data: Array<any>): EChartsOption {
    const xAxisData: Array<string> = [];
    const series: Array<any> = [
      {
        name: this.data.colDef?.headerName,
        type: 'line',
        data: [],
        smooth: true
      }
    ];

    for (const item of data) {
      xAxisData.push(item.day);
      item.value = item.value || 0;
      item.formatted_value = item.formatted_value || '0';
      series[0].data.push(item);
    }

    return {
      color: ['#3498db'],
      tooltip: {
        trigger: 'axis',
        formatter: EchartsUtils.tooltipFormatterMultiple,
        show: true,
        axisPointer: {
          type: 'cross',
          label: {
            formatter: params => {
              if (params.axisDimension === 'y') {
                return this.data.value.format_type === 'currency' &&
                  this._currencyS.format(null, params.value as number, this.data.value.currency) ||
                  this._decimalS.format(null, params.value as number);
              } else {
                return params.value.toString();
              }
            }
          }
        }
      },
      legend: {
        show: false
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: xAxisData
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: true,
          lineStyle: {
            color: '#3498db'
          }
        },
        axisLabel : {
          formatter: value => this.data.value.format_type === 'currency' &&
            this._currencyS.format(null, value, this.data.value.currency) ||
            this._decimalS.format(null, value)
        }
      },
      series: series
    };
  }

  private _getDimensions(): string {
    let str: string = '';

    for (const dimension in this.data.value.row?.dimensions || {}) {
      if (str.length && this.data.value.row.dimensions[dimension].value) {
        str += ', ';
      }
      str += this.data.value.row.dimensions[dimension].value;
    }

    return str && `(${str})` || str;
  }

}
